import { combineLatest, forkJoin, iif, Observable, of } from 'rxjs';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Subcontractor } from '../shared/subcontractor.model';
import { DisplayMode } from '../../shared/display-mode';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from '../../shared/services/navigation.service';
import { CarrierService } from '../shared/carrier.service';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { Carrier } from '../shared/carrier.model';
import { SubcontractorService } from '../shared/subcontractor.service';
import { UserService } from '../shared/user.service';
import { TranslateService } from '@ngx-translate/core';
import { NgForm } from '@angular/forms';
import { Location } from '@angular/common';
import { ModalService } from '../../shared/services/modal.service';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { TmsType } from '../shared/tms-type.model';
import { BlujayCredentials } from '../shared/blujay-credentials.model';
import { finalize, map, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';

@Component({
  selector: 'app-subcontractor-form',
  templateUrl: './subcontractor-form.component.html',
  styleUrls: ['./subcontractor-form.component.scss']
})
export class SubcontractorFormComponent implements OnInit, ComponentCanDeactivate {

  subcontractor = new Subcontractor();
  carrier: Carrier;

  TmsType     = TmsType;
  mode        = DisplayMode.Creation;
  DisplayMode = DisplayMode;

  selfCarrierMode = false;

  formProcessingBlocked      = false;
  useCustomBlujayCredentials = false;

  @ViewChild('subcontractorForm')
  form: NgForm;

  constructor(
    private route: ActivatedRoute,
    private navigation: NavigationService,
    public location: Location,
    private userService: UserService,
    private carrierService: CarrierService,
    private subcontractorService: SubcontractorService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private modalService: ModalService,
    private translate: TranslateService,
    private errorHandler: ErrorHandlerService,
  ) {
    combineLatest([this.route.params, this.route.data])
      .subscribe(([params, data]) => {
        const carrierId       = params.carrierId;
        const subcontractorId = params.subcontractorId;

        if (data.selfCarrierMode) {
          this.selfCarrierMode = true;
        }

        const newSubcontractor = of(new Subcontractor());

        const subcontractorObservable: Observable<Subcontractor> = iif(() => !!subcontractorId,
          this.subcontractorService.getById(carrierId, subcontractorId).pipe(tap(() => this.switchToUpdateMode())),
          newSubcontractor
        );

        const carrierObservable = iif(() => this.selfCarrierMode,
          this.userService.getSelf().pipe(map(currentUser => currentUser.carrier)),
          this.carrierService.getById(carrierId)
        );

        this.loader.startLoad(false);

        forkJoin([subcontractorObservable, carrierObservable])
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(([subcontractor, carrier]) => {
            this.subcontractor = subcontractor;
            this.carrier       = carrier;

            if (!this.subcontractor.blujayCredentials.isEmpty()) {
              this.useCustomBlujayCredentials = true;
            }
          }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.subcontractors.form.errors.fetch-subcontractor', false));
      });
  }

  ngOnInit() {
  }

  getForm() {
    return this.form;
  }

  switchToUpdateMode() {
    this.mode = DisplayMode.Edition;
  }

  processForm(): void {
    this.formProcessingBlocked = true;
    if (this.mode === DisplayMode.Edition) {
      this.updateSubcontractor();
    } else {
      this.createSubcontractor();
    }
  }

  createSubcontractor() {
    this.loader.startLoad();

    const createObservable = iif(() => this.selfCarrierMode,
      this.subcontractorService.createForSelfCarrier(this.subcontractor),
      this.subcontractorService.createForCarrier(this.subcontractor, this.carrier)
    );

    createObservable
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('user-dashboard.subcontractors.form.successes.create'));

        this.redirectToSubcontractorList();
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.subcontractors.form.errors.create'));
  }

  updateSubcontractor() {
    this.loader.startLoad();

    this.subcontractorService.update(this.subcontractor)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(subcontractor => {
        this.form.form.markAsPristine();
        this.subcontractor = subcontractor;

        this.toastr.success(this.translate.instant('user-dashboard.subcontractors.form.successes.update'));
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.subcontractors.form.errors.update'));
  }

  openDeleteConfirm(content) {
    this.modalService.open({
      title: this.translate.instant('user-dashboard.subcontractors.form.modals.delete.title'),
      content,
      closeButton: {
        text: this.translate.instant('user-dashboard.subcontractors.form.modals.delete.buttons.no')
      },
      validateButton: {
        type: 'danger',
        text: this.translate.instant('user-dashboard.subcontractors.form.modals.delete.buttons.yes'),
        icon: 'fa fa-trash-alt'
      },
      size: 'sm'
    }).subscribe(() => this.deleteSubcontractor());
  }

  deleteSubcontractor(): void {
    this.formProcessingBlocked = true;

    this.loader.startLoad();

    this.subcontractorService.remove(this.subcontractor)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('user-dashboard.subcontractors.form.successes.delete'));

        this.redirectToSubcontractorList();
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.subcontractors.form.errors.delete'));
  }

  trimBlujayUsername() {
    if (this.subcontractor.blujayCredentials.username) {
      this.subcontractor.blujayCredentials.username = this.subcontractor.blujayCredentials.username.trim();
    }
  }

  trimBlujayApiToken() {
    if (this.subcontractor.blujayCredentials.apiToken) {
      this.subcontractor.blujayCredentials.apiToken = this.subcontractor.blujayCredentials.apiToken.trim();
    }
  }

  useCustomBlujayCredentialsChange() {
    this.form.form.markAsDirty();

    if (!this.useCustomBlujayCredentials) {
      this.subcontractor.blujayCredentials = new BlujayCredentials();
    }
  }

  redirectToSubcontractorList() {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .list()
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(this.carrier.id)
        .list()
        .go();
    }
  }
}
